/**
 * IUPAC nucleotide codes (Cornish-Bowden 1985, NC-IUB) and strand operations.
 *
 * Shared by every sequence tool in this family. Sequences are upper-case strings; DNA
 * uses T and RNA uses U, and the complement helpers keep whichever the caller asks for.
 */

/** Each IUPAC nucleotide code and the DNA bases it stands for. */
export const NUCLEOTIDE_CODES: Readonly<Record<string, string>> = {
  A: "A",
  C: "C",
  G: "G",
  T: "T",
  U: "T",
  R: "AG",
  Y: "CT",
  S: "CG",
  W: "AT",
  K: "GT",
  M: "AC",
  B: "CGT",
  D: "AGT",
  H: "ACT",
  V: "ACG",
  N: "ACGT",
};

/** Complement of each code (DNA letters; U maps to A). */
const COMPLEMENT: Readonly<Record<string, string>> = {
  A: "T",
  T: "A",
  U: "A",
  C: "G",
  G: "C",
  R: "Y",
  Y: "R",
  K: "M",
  M: "K",
  B: "V",
  V: "B",
  D: "H",
  H: "D",
  S: "S",
  W: "W",
  N: "N",
};

const NUCLEOTIDE_LETTER = /^[ACGTURYSWKMBDHVN]$/;
const UNAMBIGUOUS = /^[ACGTU]$/;

export function isNucleotideCode(character: string): boolean {
  return NUCLEOTIDE_LETTER.test(character);
}

export function isUnambiguousBase(character: string): boolean {
  return UNAMBIGUOUS.test(character);
}

/** Complement of one code; `rna` writes A's partner as U. Unknown letters pass through. */
export function complementCode(character: string, rna = false): string {
  const partner = COMPLEMENT[character];
  if (partner === undefined) {
    return character;
  }
  return rna && partner === "T" ? "U" : partner;
}

/** The complement strand, read in the same direction as the input. */
export function complement(sequence: string, rna = false): string {
  let out = "";
  for (const character of sequence) {
    out += complementCode(character, rna);
  }
  return out;
}

export function reverse(sequence: string): string {
  return [...sequence].reverse().join("");
}

/** The reverse complement: the other strand read 5′ to 3′. */
export function reverseComplement(sequence: string, rna = false): string {
  let out = "";
  for (let i = sequence.length - 1; i >= 0; i--) {
    out += complementCode(sequence[i], rna);
  }
  return out;
}

/** Every DNA base a code can stand for, e.g. "R" → "AG". Empty for an unknown code. */
export function basesOf(code: string): string {
  return NUCLEOTIDE_CODES[code] ?? "";
}

export interface NucleotideCleanResult {
  /** Upper-case IUPAC letters only. */
  sequence: string;
  /** Distinct characters that were not nucleotide codes (and were dropped). */
  invalid: string[];
  hasT: boolean;
  hasU: boolean;
}

export interface CleanOptions {
  /** Keep lower-case letters as typed (soft-masked sequence). Default: upper-case. */
  keepCase?: boolean;
  /** Keep "-" alignment gaps instead of reporting them as invalid. */
  keepGaps?: boolean;
}

const NON_SEQUENCE_SPACE = /\s+/g;
const DIGITS = /\d+/g;

/**
 * Reduce pasted text to nucleotide letters: whitespace and position numbers (as in a
 * GenBank listing) are removed silently; anything else that is not an IUPAC nucleotide
 * code is dropped and reported.
 */
export function cleanNucleotides(
  raw: string,
  options: CleanOptions = {}
): NucleotideCleanResult {
  const squeezed = raw.replace(NON_SEQUENCE_SPACE, "").replace(DIGITS, "");
  const stripped = options.keepCase ? squeezed : squeezed.toUpperCase();
  const kept: string[] = [];
  const invalid = new Set<string>();
  for (const character of stripped) {
    if (
      isNucleotideCode(character.toUpperCase()) ||
      (options.keepGaps && character === "-")
    ) {
      kept.push(character);
    } else {
      invalid.add(character);
    }
  }
  const sequence = kept.join("");
  const upper = options.keepCase ? sequence.toUpperCase() : sequence;
  return {
    sequence,
    invalid: [...invalid],
    hasT: upper.includes("T"),
    hasU: upper.includes("U"),
  };
}
